/**
 * useVoiceRecorder — Web MediaRecorder API hook for voice messages.
 *
 * Features:
 * - Secure context guard (mediaDevices existence check)
 * - Proactive permission check via Permissions API with onchange auto-resume
 * - Device availability check after getUserMedia
 * - Console breadcrumbs at every decision point for debugging
 * - Distinct error codes for every failure mode
 * - AudioContext + AnalyserNode for live frequency data (32 bins)
 * - Waveform peaks sampling every 100ms for playback visualization
 * - Duration tracking, 60-second max, cleanup on unmount
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export type RecordingStatus = 'idle' | 'requesting' | 'recording' | 'processing';

/**
 * 'unknown' = haven't checked yet
 * 'prompt'  = browser will show the permission dialog
 * 'granted' = user allowed mic
 * 'denied'  = user blocked mic (must fix in browser settings)
 */
export type MicPermissionState = 'unknown' | 'prompt' | 'granted' | 'denied';

/** Semantic error codes set by the hook — the component maps these to UI. */
export type MicErrorCode =
  | 'MIC_BLOCKED'         // Permission denied — must fix in browser settings
  | 'MIC_NO_SECURE_CTX'   // navigator.mediaDevices undefined (HTTP, not localhost)
  | 'MIC_NOT_FOUND'       // No audioinput device
  | 'MIC_OVERCONSTRAINED' // Requested constraints can't be met
  | 'MIC_IN_USE'          // Another app has exclusive lock on the mic
  | 'MIC_UNKNOWN';        // Unexpected error

export interface VoiceRecorderState {
  status: RecordingStatus;
  duration: number; // seconds
  micPermission: MicPermissionState;
  error: MicErrorCode | null;
  errorDetail: string | null; // raw error message for debugging
  frequencyBins: number[]; // live 32-bin frequency data (0-255)
}

export interface VoiceRecorderResult {
  blob: Blob;
  url: string; // object URL for playback
  mimeType: string;
  duration: number; // seconds
  waveformPeaks: number[]; // real amplitude peaks (0–1 normalized)
}

export interface UseVoiceRecorderReturn {
  state: VoiceRecorderState;
  startRecording: () => Promise<boolean>;
  stopRecording: () => Promise<VoiceRecorderResult | null>;
  cancelRecording: () => void;
}

const MAX_DURATION_SECONDS = 60;
const PEAK_SAMPLE_INTERVAL_MS = 100;
const LOG_PREFIX = '[VoiceRecorder]';

// Prefer webm (Chrome/Firefox), fallback to mp4 (Safari)
function getPreferredMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
  return '';
}

/** Check if we're in a secure context where mediaDevices is available */
function hasMediaDevices(): boolean {
  return typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getUserMedia === 'function';
}

/** Query permission state, returns 'unknown' if Permissions API unavailable */
async function queryMicPermission(): Promise<MicPermissionState> {
  if (!navigator.permissions) return 'unknown';
  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state as MicPermissionState;
  } catch {
    return 'unknown';
  }
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const [state, setState] = useState<VoiceRecorderState>({
    status: 'idle',
    duration: 0,
    micPermission: 'unknown',
    error: null,
    errorDetail: null,
    frequencyBins: [],
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);
  const resolveStopRef = useRef<((result: VoiceRecorderResult | null) => void) | null>(null);

  // Web Audio API refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const frequencyDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const timeDomainDataRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const waveformPeaksRef = useRef<number[]>([]);
  const peakIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Store latest micPermission in a ref so startRecording always reads current value
  // (avoids stale closure when called from mount effect)
  const micPermissionRef = useRef<MicPermissionState>(state.micPermission);
  micPermissionRef.current = state.micPermission;

  // =========================================================================
  // Proactive permission monitoring via Permissions API
  // Subscribes to onchange so the UI auto-updates when the user flips the
  // switch in browser settings — no page refresh needed.
  // =========================================================================
  useEffect(() => {
    if (!navigator.permissions) {
      console.warn(LOG_PREFIX, 'Permissions API not available — cannot monitor mic permission changes');
      return;
    }

    let permStatus: PermissionStatus | null = null;

    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        permStatus = status;
        console.info(LOG_PREFIX, 'Initial mic permission:', status.state);
        setState((prev) => ({ ...prev, micPermission: status.state as MicPermissionState }));

        status.onchange = () => {
          const newState = status.state as MicPermissionState;
          console.info(LOG_PREFIX, 'Mic permission changed →', newState);
          setState((prev) => ({
            ...prev,
            micPermission: newState,
            // Clear the error when user grants permission in settings
            ...(newState === 'granted' ? { error: null, errorDetail: null } : {}),
          }));
        };
      })
      .catch((err) => {
        console.warn(LOG_PREFIX, 'Permissions API query failed:', err);
      });

    return () => {
      if (permStatus) permStatus.onchange = null;
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      if (peakIntervalRef.current) clearInterval(peakIntervalRef.current);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  const startRafLoop = useCallback(() => {
    const analyser = analyserRef.current;
    const dataArray = frequencyDataRef.current;
    if (!analyser || !dataArray) return;

    function tick() {
      analyser!.getByteFrequencyData(dataArray!);
      setState((prev) => ({ ...prev, frequencyBins: Array.from(dataArray!) }));
      rafIdRef.current = requestAnimationFrame(tick);
    }
    rafIdRef.current = requestAnimationFrame(tick);
  }, []);

  const stopRafLoop = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const startPeakSampling = useCallback(() => {
    waveformPeaksRef.current = [];
    peakIntervalRef.current = setInterval(() => {
      const analyser = analyserRef.current;
      const timeDomainData = timeDomainDataRef.current;
      if (!analyser || !timeDomainData) return;

      analyser.getByteTimeDomainData(timeDomainData);
      let max = 0;
      for (let i = 0; i < timeDomainData.length; i++) {
        const v = Math.abs(timeDomainData[i] - 128) / 128;
        if (v > max) max = v;
      }
      waveformPeaksRef.current.push(max);
    }, PEAK_SAMPLE_INTERVAL_MS);
  }, []);

  const stopPeakSampling = useCallback(() => {
    if (peakIntervalRef.current) {
      clearInterval(peakIntervalRef.current);
      peakIntervalRef.current = null;
    }
  }, []);

  const startDurationTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    durationIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setState((prev) => {
        if (elapsed >= MAX_DURATION_SECONDS) {
          mediaRecorderRef.current?.stop();
          return { ...prev, duration: MAX_DURATION_SECONDS };
        }
        return { ...prev, duration: elapsed };
      });
    }, 100);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const cleanupAudio = useCallback(() => {
    stopRafLoop();
    stopPeakSampling();
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    frequencyDataRef.current = null;
    timeDomainDataRef.current = null;
  }, [stopRafLoop, stopPeakSampling]);

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    cleanupAudio();
  }, [cleanupAudio]);

  const setError = useCallback((code: MicErrorCode, detail: string, perm?: MicPermissionState) => {
    console.warn(LOG_PREFIX, `Error: ${code} — ${detail}`);
    setState((prev) => ({
      ...prev,
      status: 'idle',
      error: code,
      errorDetail: detail,
      frequencyBins: [],
      ...(perm != null ? { micPermission: perm } : {}),
    }));
  }, []);

  const startRecording = useCallback(async (): Promise<boolean> => {
    try {
      // ── Guard 1: Secure context ──
      if (!hasMediaDevices()) {
        console.error(LOG_PREFIX, 'navigator.mediaDevices is undefined — not a secure context?');
        console.error(LOG_PREFIX, 'Protocol:', window.location.protocol, 'Host:', window.location.host);
        setError('MIC_NO_SECURE_CTX', `mediaDevices unavailable (${window.location.protocol}//${window.location.host})`);
        return false;
      }

      setState((prev) => ({ ...prev, status: 'requesting', error: null, errorDetail: null, frequencyBins: [] }));

      // ── Guard 2: Already known denied ──
      // Read from ref (not closure) to avoid stale value from mount effect
      const currentPerm = micPermissionRef.current;
      console.info(LOG_PREFIX, 'startRecording — micPermission:', currentPerm);

      if (currentPerm === 'denied') {
        console.warn(LOG_PREFIX, 'Mic permission is denied — not calling getUserMedia');
        setError('MIC_BLOCKED', 'Permission denied (cached state)', 'denied');
        return false;
      }

      // ── Request microphone access ──
      // No extra async hops before getUserMedia — go straight to the browser
      console.info(LOG_PREFIX, 'Calling getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.info(LOG_PREFIX, 'getUserMedia succeeded, tracks:', stream.getTracks().length);
      streamRef.current = stream;

      // Set up Web Audio API for live frequency data
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      analyserRef.current = analyser;
      frequencyDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      timeDomainDataRef.current = new Uint8Array(analyser.fftSize);

      // Set up MediaRecorder
      const mimeType = getPreferredMimeType();
      console.info(LOG_PREFIX, 'MediaRecorder mimeType:', mimeType || '(default)');
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stopDurationTimer();
        stopRafLoop();
        stopPeakSampling();

        const actualMime = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: actualMime });
        const url = URL.createObjectURL(blob);
        const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const waveformPeaks = [...waveformPeaksRef.current];

        cleanupAudio();

        if (resolveStopRef.current) {
          resolveStopRef.current({
            blob,
            url,
            mimeType: actualMime,
            duration: Math.max(1, duration),
            waveformPeaks,
          });
          resolveStopRef.current = null;
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }

        setState((prev) => ({ ...prev, status: 'idle', duration: 0, frequencyBins: [] }));
      };

      recorder.onerror = () => {
        stopDurationTimer();
        cleanupStream();
        setError('MIC_UNKNOWN', 'MediaRecorder error event');
        if (resolveStopRef.current) {
          resolveStopRef.current(null);
          resolveStopRef.current = null;
        }
      };

      recorder.start(100);
      startDurationTimer();
      startRafLoop();
      startPeakSampling();

      console.info(LOG_PREFIX, 'Recording started successfully');

      setState((prev) => ({
        ...prev,
        status: 'recording',
        duration: 0,
        micPermission: 'granted',
        error: null,
        errorDetail: null,
      }));

      return true;
    } catch (error) {
      console.error(LOG_PREFIX, 'startRecording caught error:', error);
      cleanupStream();

      // Re-check actual permission state after failure
      const realPermState = await queryMicPermission();
      console.info(LOG_PREFIX, 'Post-error permission state:', realPermState);

      // Classify: is this a permission problem?
      const isNotAllowed = error instanceof DOMException && error.name === 'NotAllowedError';
      const isDenied = realPermState === 'denied' || isNotAllowed;

      if (isDenied) {
        setError('MIC_BLOCKED', `${error instanceof Error ? error.message : String(error)} (perm: ${realPermState})`, 'denied');
        return false;
      }

      // Classify specific DOMException types
      if (error instanceof DOMException) {
        switch (error.name) {
          case 'NotFoundError':
            setError('MIC_NOT_FOUND', error.message, realPermState);
            return false;
          case 'OverconstrainedError':
            setError('MIC_OVERCONSTRAINED', error.message, realPermState);
            return false;
          case 'NotReadableError':
            setError('MIC_IN_USE', error.message, realPermState);
            return false;
        }
      }

      // Catch-all: unknown error
      const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      setError('MIC_UNKNOWN', detail, realPermState);
      return false;
    }
  }, [setError, startDurationTimer, stopDurationTimer, cleanupStream, cleanupAudio, startRafLoop, stopRafLoop, startPeakSampling, stopPeakSampling]);

  const stopRecording = useCallback((): Promise<VoiceRecorderResult | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }

      setState((prev) => ({ ...prev, status: 'processing' }));
      resolveStopRef.current = resolve;
      mediaRecorderRef.current.stop();
    });
  }, []);

  const cancelRecording = useCallback(() => {
    resolveStopRef.current = null;
    stopDurationTimer();
    stopRafLoop();
    stopPeakSampling();

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {
        cleanupStream();
      };
      mediaRecorderRef.current.stop();
    } else {
      cleanupStream();
    }

    chunksRef.current = [];
    waveformPeaksRef.current = [];
    setState((prev) => ({ ...prev, status: 'idle', duration: 0, error: null, errorDetail: null, frequencyBins: [] }));
  }, [stopDurationTimer, stopRafLoop, stopPeakSampling, cleanupStream]);

  return {
    state,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}

/** Format duration in M:SS format */
export function formatRecordingDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
